import type { Calling, Client, Provider } from '@accounts/transport';
import { Legal } from './legal';
import type { ProductHoldings } from './members';
import type { Account, Identity, Link, Providers, Session } from './types';

/**
 * Signing in with a provider's token, and everything a person may do about their own account. Password
 * operations go through the service (`Credentials`), never from this browser to the provider.
 *
 * This is a plain class with no framework in it, so the rules it encodes — that a provider token is
 * used once, that preferences are not authorization, that removing the last sign-in method is refused
 * by the service and not hidden by the interface — are testable without rendering anything.
 */
export class Accounts {
	#client: Client;
	#provider: Provider;
	/** The published versions the sign-in screen presents, once the service has said which they are */
	#legal?: Providers['legal'];
	#published: Legal;

	/**
	 * @param published Where the documents are published. Versions are presented, and recorded, only
	 * when the sign-in screen can link to them
	 */
	constructor(client: Client, provider: Provider, published = new Legal()) {
		this.#client = client;
		this.#provider = provider;
		this.#published = published;
	}

	/** What the sign-in screen may know before anyone has signed in */
	async providers(): Promise<Providers> {
		const providers = await this.#client.get<Providers>('/v1/providers');
		this.#legal = providers.legal;
		return providers;
	}

	/**
	 * Who is signed in, or a refusal.
	 *
	 * @param calling `{ended: 'caller'}` for a read that decides standing itself: the session's own read,
	 * and the reads after a person ended their session here
	 */
	async identity(calling?: Calling): Promise<Identity> {
		return this.#client.get<Identity>('/v1/identity', calling);
	}

	async account(): Promise<{ account: Account; identities: Link[] }> {
		return this.#client.get<{ account: Account; identities: Link[] }>('/v1/account');
	}

	/**
	 * Exchanges a provider token for an Accounts session, or for the second step of an account with a
	 * second factor (`factor`). The token is passed straight through and is never stored on the way.
	 */
	async enter(token: string): Promise<{ account?: Account; created?: boolean; factor?: { ticket: string; methods: string[]; expires: string } }> {
		// The versions presented are the ones the service publishes; a screen that never read them
		// (a confirmation link opened directly) asks once, so what is recorded is what was shown
		if (this.#published.published && this.#legal === undefined) await this.providers().catch(() => undefined);
		const agreements = this.#published.published && (this.#legal?.terms || this.#legal?.privacy) ? this.#legal : undefined;
		// The token is the credential here: a bare UNAUTHENTICATED is about it, not about a session
		return this.#client.post<{ account?: Account; created?: boolean; factor?: { ticket: string; methods: string[]; expires: string } }>('/v1/sessions', { token, agreements }, { ended: 'token' });
	}

	/** Exchanges the authorization code a provider returned for its access token */
	async exchange(code: string): Promise<string> {
		return this.#provider.exchange(code);
	}

	async prefer(input: { name?: string; locale?: string; appearance?: string }): Promise<{ account: Account }> {
		return this.#client.patch<{ account: Account }>('/v1/account', input);
	}

	async attach(token: string): Promise<{ identities: Link[] }> {
		return this.#client.post<{ identities: Link[] }>('/v1/account/identities', { token }, { ended: 'token' });
	}

	async detach(provider: string, subject: string): Promise<{ identities: Link[] }> {
		// Confirmed with its consequence: never sent again after a renewal, the person presses it again
		return this.#client.delete<{ identities: Link[] }>(`/v1/account/identities/${encodeURIComponent(provider)}/${encodeURIComponent(subject)}`, { replay: false });
	}

	/** Everything Accounts holds about this person, as the service exports it */
	async copy(): Promise<Record<string, unknown>> {
		return this.#client.get<Record<string, unknown>>('/v1/account/export');
	}

	/**
	 * Erases this account. The service needs the account's own identifier as confirmation, so nothing
	 * reaches this by accident; the screen asks the person to confirm before calling it.
	 */
	async erase(account: string): Promise<{ erasure: { scheduled?: string; id?: string } }> {
		return this.#client.post<{ erasure: { scheduled?: string; id?: string } }>('/v1/account/erasure', { confirm: account }, { replay: false });
	}

	/** What each product holds for the person, and the organizations that block deleting the account (ACC-08) */
	async holdings(): Promise<{ holdings: ProductHoldings[]; blocking: { id: string; name: string }[] }> {
		return this.#client.get<{ holdings: ProductHoldings[]; blocking: { id: string; name: string }[] }>('/v1/account/holdings', { ended: 'caller' });
	}

	/** Keeps an account scheduled for deletion (E8) */
	async keep(): Promise<void> {
		await this.#client.delete('/v1/account/erasure');
	}

	async sessions(): Promise<{ sessions: Session[] }> {
		return this.#client.get<{ sessions: Session[] }>('/v1/sessions');
	}

	/** Ends this surface only. An answer that the session had already ended is the caller's: it is out either way */
	async leave(): Promise<{ ended: number }> {
		return this.#client.delete<{ ended: number }>('/v1/sessions/current', { ended: 'caller' });
	}

	/**
	 * Ends every session of this account, everywhere; confirmed with its consequence, so never sent again.
	 * With `access`, the person's AI apps and access tokens lose their access too (D196).
	 */
	async leaveEverywhere(access = false): Promise<{ ended: number }> {
		return this.#client.delete<{ ended: number }>(access ? '/v1/sessions?access=end' : '/v1/sessions', { replay: false });
	}

	async select(organization: string | undefined): Promise<{ organization?: string }> {
		return this.#client.post<{ organization?: string }>('/v1/sessions/current/organization', { organization: organization ?? null });
	}
}
